import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Ticket, Copy, Plus, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export function AccessCodes() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [count, setCount] = useState(30);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/promo-codes");
      setData(data);
    } catch { setData(null); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/admin/promo-codes", { count: Number(count) || 1, prefix: "GAFFER" });
      toast.success(`${count} season-pass codes created`);
      await load();
    } catch { toast.error("Couldn't generate codes"); }
    finally { setBusy(false); }
  };

  const available = (data?.codes || []).filter((c) => !c.redeemed).map((c) => c.code);
  const copyAll = async () => {
    try { await navigator.clipboard.writeText(available.join("\n")); toast.success(`Copied ${available.length} codes`); }
    catch { toast.error("Copy failed"); }
  };
  const copyOne = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success("Copied"); } catch { /* noop */ }
  };
  const claimLink = (code) => `${window.location.origin}/api/claim/${code}`;
  const inviteMessage = (code) =>
    `You're in! ⚽ I've got you free lifetime access to GAFFER — predict every scoreline and take on the family table. Tap to claim & sign up: ${claimLink(code)}`;
  const shareWhatsApp = (code) =>
    window.open(`https://wa.me/?text=${encodeURIComponent(inviteMessage(code))}`, "_blank");
  const copyMessage = async (code) => {
    try { await navigator.clipboard.writeText(inviteMessage(code)); toast.success("Invite message copied"); }
    catch { toast.info(inviteMessage(code)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="access-codes-btn"
          title="Manage free-access (season pass) codes"
          className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 border border-border text-[10px] uppercase tracking-wider text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors">
          <Ticket size={12} /> Codes{data ? ` · ${data.available}` : ""}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card border-2 border-foreground rounded-none max-w-lg" data-testid="access-codes-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase flex items-center gap-2">
            <Ticket className="text-[hsl(var(--stadium-yellow))]" size={24} /> Free-Access Codes
          </DialogTitle>
          <DialogDescription className="font-data text-xs">
            Single-use lifetime passes (never expire). Share a code's one-tap claim link — friends sign up and get Premium instantly.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-2">
          <div>
            <p className="overline text-muted-foreground mb-1">How many</p>
            <Input type="number" min={1} max={200} value={count}
              onChange={(e) => setCount(e.target.value)} data-testid="codes-count-input"
              className="rounded-none w-24 font-data" />
          </div>
          <Button onClick={generate} disabled={busy} data-testid="codes-generate-btn"
            className="rounded-none bg-[hsl(var(--stadium-yellow))] text-black hover:bg-[hsl(var(--stadium-yellow))]/90 uppercase text-xs font-bold">
            <Plus size={14} className="me-1" /> {busy ? "…" : "Generate"}
          </Button>
          {data && (
            <div className="ms-auto text-right font-data text-[11px] text-muted-foreground">
              <span className="text-[hsl(var(--pitch-green))]">{data.available}</span> available · {data.used} used · {data.total} total
            </div>
          )}
        </div>

        {available.length > 0 && (
          <>
            <div className="flex items-center justify-between mt-2">
              <p className="overline text-muted-foreground">Available codes</p>
              <Button size="sm" variant="outline" onClick={copyAll} data-testid="codes-copyall-btn" className="rounded-none">
                <Copy size={13} className="me-1" /> Copy all
              </Button>
            </div>
            <div className="max-h-64 overflow-auto border-2 border-border divide-y divide-border" data-testid="codes-list">
              {available.map((code) => (
                <div key={code} className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-muted/40 transition-colors">
                  <button onClick={() => copyOne(code)} title="Copy code"
                    className="font-data text-sm tracking-widest inline-flex items-center gap-1.5">
                    <Copy size={12} className="text-muted-foreground" /> {code}
                  </button>
                  <div className="flex items-center gap-1">
                    <button onClick={() => copyMessage(code)} data-testid={`code-msg-${code}`} title="Copy ready-made invite message"
                      className="inline-flex items-center gap-1 px-2 py-1 border border-border text-[10px] uppercase tracking-wider hover:border-primary/60 transition-colors">
                      <Copy size={11} /> Msg
                    </button>
                    <button onClick={() => shareWhatsApp(code)} data-testid={`code-wa-${code}`} title="Share via WhatsApp"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-[hsl(145_63%_42%)] text-white text-[10px] uppercase tracking-wider hover:brightness-110 transition-all">
                      <MessageCircle size={11} /> WhatsApp
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
